import { useEffect } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useClonePolicyConfiguration, type PolicyConfigurationRow } from './BenefitPolicyService'
import { ExistingDraftNotice } from './ExistingDraftNotice'
import { yupSiblingString } from '~/shared/lib/guards'

/**
 * Open next plan year from an existing configuration.
 *
 * The dates are pre-filled from the source shifted one year, which is a starting point rather than
 * a rule — open enrollment moves, and the operator adjusts. An overlapping period is deliberately
 * **not** blocked here: two concurrent configurations on one contract is the case this project
 * exists to support, so the server treats an overlap as a warning and so does this dialog.
 */
interface ClonePlanYearDialogProps {
  open: boolean
  /** Rows the operator may clone from — the list already filters to `can_clone`. */
  sources: PolicyConfigurationRow[]
  /** Pre-selected source when the dialog was opened from a row's Clone button. */
  initialSourceId?: number
  onClose: () => void
  onCloned: (created: { id: number; effective_start: string | null }) => void
}

/** Same calendar day one year on. Feb 29 lands on Mar 1, which is the sane renewal reading. */
const shiftOneYear = (iso: string | null): string => {
  if (!iso) return ''
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return ''
  return new Date(Date.UTC(year + 1, month - 1, day)).toISOString().slice(0, 10)
}

const validationSchema = Yup.object({
  source_configuration: Yup.number().required('Pick a policy to clone.'),
  effective_start: Yup.string().required('An effective start date is required.'),
  effective_end: Yup.string()
    .required('An effective end date is required.')
    .test(
      'after-start',
      'The effective end date must be after the effective start date.',
      (value, context) => {
        const start = yupSiblingString(context, 'effective_start')
        return !value || !start || value > start
      },
    ),
  electable_start_date: Yup.string().required('An electable start date is required.'),
})

export const ClonePlanYearDialog = ({
  open,
  sources,
  initialSourceId,
  onClose,
  onCloned,
}: ClonePlanYearDialogProps) => {
  const clonePolicy = useClonePolicyConfiguration()

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      source_configuration: initialSourceId ?? '',
      effective_start: '',
      effective_end: '',
      electable_start_date: '',
      copy_accumulators: true,
      copy_pharmacy: true,
      copy_benefit_information: true,
      copy_point_solutions: true,
    },
    validationSchema,
    onSubmit: async values => {
      const created = await clonePolicy.mutateAsync({
        source_configuration: Number(values.source_configuration),
        effective_start: values.effective_start,
        effective_end: values.effective_end,
        electable_start_date: values.electable_start_date,
        copy_accumulators: values.copy_accumulators,
        copy_pharmacy: values.copy_pharmacy,
        copy_benefit_information: values.copy_benefit_information,
        copy_point_solutions: values.copy_point_solutions,
      })
      formik.resetForm()
      onCloned({ id: created.id, effective_start: created.effective_start })
    },
  })

  const { setValues } = formik
  const selectedId = formik.values.source_configuration
  const selectedSource = sources.find(row => row.id === Number(selectedId))

  const prefillStart = shiftOneYear(selectedSource?.effective_start ?? null)
  const prefillEnd = shiftOneYear(selectedSource?.effective_end ?? null)
  const prefillElectable = shiftOneYear(selectedSource?.electable_start_date ?? null)

  // Re-prefill whenever the source changes, so switching sources in the dropdown moves the dates
  // with it rather than leaving last selection's dates behind.
  useEffect(() => {
    // Empty until a source resolves and carries a start date, which `can_clone` guarantees.
    if (!open || !prefillStart) return
    void setValues(current => ({
      ...current,
      effective_start: prefillStart,
      effective_end: prefillEnd,
      electable_start_date: prefillElectable,
    }))
  }, [open, prefillStart, prefillEnd, prefillElectable, setValues])

  const handleClose = () => {
    formik.resetForm()
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>Clone a plan year</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Creates a draft pre-filled from an existing policy. Nothing is member-visible until it
            is published.
          </DialogContentText>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              required
              name="source_configuration"
              label="Source policy"
              value={formik.values.source_configuration}
              onChange={formik.handleChange}
              error={formik.touched.source_configuration && !!formik.errors.source_configuration}
              helperText={formik.touched.source_configuration && formik.errors.source_configuration}
              data-testid="clone-source"
            >
              {sources.map(row => (
                <MenuItem key={row.id} value={row.id}>
                  {row.contract_name} — {row.effective_start ?? 'no start date'}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                required
                fullWidth
                type="date"
                name="effective_start"
                label="New effective start"
                slotProps={{ inputLabel: { shrink: true } }}
                value={formik.values.effective_start}
                onChange={formik.handleChange}
                error={formik.touched.effective_start && !!formik.errors.effective_start}
                helperText={formik.touched.effective_start && formik.errors.effective_start}
                data-testid="clone-effective-start"
              />
              <TextField
                required
                fullWidth
                type="date"
                name="effective_end"
                label="New effective end"
                slotProps={{ inputLabel: { shrink: true } }}
                value={formik.values.effective_end}
                onChange={formik.handleChange}
                error={formik.touched.effective_end && !!formik.errors.effective_end}
                helperText={formik.touched.effective_end && formik.errors.effective_end}
                data-testid="clone-effective-end"
              />
            </Stack>
            <TextField
              required
              type="date"
              name="electable_start_date"
              label="Electable start (open enrollment)"
              slotProps={{ inputLabel: { shrink: true } }}
              value={formik.values.electable_start_date}
              onChange={formik.handleChange}
              error={formik.touched.electable_start_date && !!formik.errors.electable_start_date}
              helperText={formik.touched.electable_start_date && formik.errors.electable_start_date}
              data-testid="clone-electable-start"
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Pre-filled from the source shifted one year — adjust to this year&apos;s actual open
              enrollment dates.
            </Typography>
            <FormGroup>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Copy into the new draft
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    name="copy_accumulators"
                    checked={formik.values.copy_accumulators}
                    onChange={formik.handleChange}
                    slotProps={{ input: { 'aria-label': 'Copy accumulators' } }}
                  />
                }
                label="Accumulators"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="copy_pharmacy"
                    checked={formik.values.copy_pharmacy}
                    onChange={formik.handleChange}
                    slotProps={{ input: { 'aria-label': 'Copy pharmacy' } }}
                  />
                }
                label="Pharmacy &amp; PBM"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="copy_benefit_information"
                    checked={formik.values.copy_benefit_information}
                    onChange={formik.handleChange}
                    slotProps={{ input: { 'aria-label': 'Copy benefit information' } }}
                  />
                }
                label="Benefit information"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="copy_point_solutions"
                    checked={formik.values.copy_point_solutions}
                    onChange={formik.handleChange}
                    slotProps={{ input: { 'aria-label': 'Copy additional benefits' } }}
                  />
                }
                label="Additional benefits"
              />
            </FormGroup>
            <ExistingDraftNotice open={open} contractId={selectedSource?.contract} />
            <Alert severity="info">
              Plan documents and the benefits grid are not copied — set those up for the new plan
              year separately. The new draft also starts without a plan id. Copied benefit
              information keeps its text but not its document link, since the documents stay behind.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
            data-testid="clone-submit"
          >
            Create draft
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
